import { Component, computed, effect, HostListener, inject, input, output, signal, ViewChild, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenavModule } from '@angular/material/sidenav';
import { TranslateService } from '@ngx-translate/core';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CopyImageAndCreateChunkData } from 'src/app/views/content/chunk/chunk.dto';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentGeneratorComponent } from 'src/app/views/content/content-generator/content-generator.component';
import { ContentRefinerComponent } from 'src/app/views/content/content-refiner/content-refiner.component';
import { AIPaneData, ContentStateService } from 'src/app/views/content/content-state.service';
import { GenerateContentPaneComponent } from 'src/app/views/content/generate-content-pane/generate-content-pane.component';
import { ImageGeneratorComponent } from 'src/app/views/content/image-generator/image-generator.component';
import { ImageRefinerComponent } from 'src/app/views/content/image-refiner/image-refiner.component';
import { MermaidGeneratorComponent } from 'src/app/views/content/mermaid-generator/mermaid-generator.component';
import { QuizGeneratorComponent } from 'src/app/views/content/quiz-generator/quiz-generator.component';
import { QuestionsAnalysisSidebarComponent } from 'src/app/views/questions/questions-analysis-sidebar/questions-analysis-sidebar.component';
import { QuestionsSearchSidebarComponent } from 'src/app/views/questions/questions-search-sidebar/questions-search-sidebar.component';
import { VideoCallStateService } from '../video-call-state.service';

@Component({
  selector: 'app-content-shell',
  imports: [
    MatSidenavModule,
    GenerateContentPaneComponent,
    QuizGeneratorComponent,
    ContentGeneratorComponent,
    ContentRefinerComponent,
    ImageGeneratorComponent,
    ImageRefinerComponent,
    MermaidGeneratorComponent,
    QuestionsAnalysisSidebarComponent,
    QuestionsSearchSidebarComponent,
  ],
  template: `
    <mat-drawer-container [@.disabled]="isSmallOrTablet()" [hasBackdrop]="isSmallOrTablet()" class="drawer-container" autosize>
      <mat-drawer
        #drawer
        [mode]="drawerMode()"
        position="end"
        [opened]="isDrawerOpen()"
        (closed)="closeAllContentPanes()"
        class="drawer"
        [class.generator-mode]="activeMode() !== 'ai' && activeMode() !== null"
        [class.ai-drawer]="
          activeMode() === 'ai' ||
          activeMode() === 'quiz' ||
          activeMode() === 'generator' ||
          activeMode() === 'mermaid-generator' ||
          activeMode() === 'refiner' ||
          activeMode() === 'image-generator' ||
          activeMode() === 'image-refiner' ||
          activeMode() === 'questions-analysis' ||
          activeMode() === 'questions-search-sidebar'
        "
      >
        @if (activeMode() === 'ai') {
          <div class="ai-drawer-content">
            <app-generate-content-pane
              [contentID]="contentId()"
              [paneData]="aiPaneData()"
              [settings]="settings()"
              (settingsChanged)="settingsChanged.emit($event)"
              (closed)="closeAiPane()"
            />
          </div>
        } @else if (activeMode() === 'quiz') {
          <div class="ai-drawer-content">
            <!-- reusing same container style -->
            <app-quiz-generator [contentID]="contentId()" (close)="closeQuizPane()" />
          </div>
        } @else if (activeMode() === 'generator') {
          <div class="ai-drawer-content">
            <app-content-generator [courseID]="contentId()" (close)="closeGeneratorPane()" />
          </div>
        } @else if (activeMode() === 'mermaid-generator') {
          <div class="ai-drawer-content">
            <app-mermaid-generator [courseID]="contentId()" (close)="closeMermaidGeneratorPane()" />
          </div>
        } @else if (activeMode() === 'refiner') {
          <div class="ai-drawer-content">
            <app-content-refiner [draft]="refinerDraft()!" (close)="closeRefinerPane()" (updated)="handleRefinerUpdate($event)" />
          </div>
        } @else if (activeMode() === 'image-generator') {
          <div class="ai-drawer-content">
            <app-image-generator [courseId]="contentId()" (close)="closeImageGeneratorPane()" />
          </div>
        } @else if (activeMode() === 'image-refiner') {
          <div class="ai-drawer-content">
            <app-image-refiner
              [draft]="imageRefinerDraft()!"
              (close)="closeImageRefinerPane()"
              (updated)="handleImageRefinerUpdate($event)"
              (imageSelected)="handleImageSelection($event)"
            />
          </div>
        } @else if (activeMode() === 'questions-analysis') {
          <div class="ai-drawer-content">
            <app-questions-analysis-sidebar (close)="closeQuestionsAnalysisPane()" />
          </div>
        } @else if (activeMode() === 'questions-search-sidebar') {
          <div class="ai-drawer-content">
            <app-questions-search-sidebar (close)="closeQuestionsSearchSidebarPane()" />
          </div>
        }
      </mat-drawer>
      <mat-drawer-content class="content-page">
        <ng-content></ng-content>
      </mat-drawer-content>
    </mat-drawer-container>
  `,
  styles: `
    .drawer-container {
      height: 100vh;
      background-color: transparent;

      .drawer {
        width: 100%;
        max-width: 360px;
        box-sizing: border-box;

        &.zoom-recording-disabled {
          width: 340px;
        }

        &.ai-drawer {
          max-width: 450px;
          padding: 15px 12px 9px 12px;
          border: none;
          box-sizing: border-box;
          background-color: transparent;

          .ai-drawer-content {
            height: 100%;
            overflow: hidden;
            background-color: #ffffff;
            border: 1px solid #eee;
            border-radius: var(--skz-radius-medium);
            box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.055);
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
          }
        }

        /* Responsive width */
        @media (max-width: 840px) {
          width: 100% !important;
          max-width: 100%;

          &.ai-drawer:not(.generator-mode) {
            width: 100% !important;
            padding: 10px;

            transform: translate3d(100%, 0, 0) !important;
            transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;

            &.mat-drawer-opened {
              transform: translate3d(0, 0, 0) !important;
            }
          }

          &.generator-mode {
            width: 50% !important;
            max-width: 50% !important;
            min-width: 50% !important;

            transform: translate3d(100%, 0, 0) !important;
            transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;

            &.mat-drawer-opened {
              transform: translate3d(0, 0, 0) !important;
            }
          }
        }

        @media (max-width: 599px) {
          &.ai-drawer:not(.generator-mode) {
            padding: 0;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 100% !important;
            height: 100% !important;
            top: 0 !important;
            bottom: 0 !important;
            left: auto !important;
            right: 0 !important;

            transform: translate3d(100%, 0, 0) !important;
            transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;

            &.mat-drawer-opened {
              transform: translate3d(0, 0, 0) !important;
            }

            .ai-drawer-content {
              border-radius: 0;
              border: none;
              box-shadow: none;
            }
          }

          &.generator-mode {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 100% !important;
            height: 50vh !important;
            max-height: 50vh !important;
            top: auto !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            padding: 0 !important;
            border-radius: 20px 20px 0 0 !important;

            transform: translate3d(0, 100%, 0) !important;
            transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;

            &.mat-drawer-opened {
              transform: translate3d(0, 0, 0) !important;
            }

            .ai-drawer-content {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              height: 100% !important;
              border-radius: 20px 20px 0 0 !important;
              border: none !important;
              box-shadow: none !important;

              ::ng-deep {
                .content-refiner-container,
                .content-generator-container,
                .quiz-generator-container,
                .image-generator-container,
                .image-refiner-container,
                .mermaid-generator-container {
                  overflow-y: auto !important;
                  display: flex !important;
                  flex-direction: column !important;
                  height: 100% !important;

                  .header {
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                    background-color: #fff !important;
                    flex-shrink: 0 !important;
                  }

                  .content {
                    flex: none !important;
                    overflow-y: visible !important;
                  }

                  .refiner-footer,
                  .footer {
                    position: relative !important;
                    flex-shrink: 1 !important;
                    margin-top: auto !important;
                  }
                }
              }
            }
          }
        }
      }
    }

    .mat-drawer.mat-drawer-end {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  `,
})
export class ContentShellComponent {
  private videoCallStateService = inject(VideoCallStateService);
  private contentStateService = inject(ContentStateService);
  private chunkService = inject(ChunkService);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  private errorHandler = inject(ErrorHandlerService);
  private breakpointObserver = inject(BreakpointObserverService);

  contentId = input.required<string>();
  settings = input<any>(null); // Accepts CourseSettings or any settings object
  settingsChanged = output<any>();

  // Video State
  videoDrawerOpened = this.videoCallStateService.drawerOpened;

  // AI State
  aiPaneData = signal<AIPaneData | null>(null);
  aiPaneOpen = signal(false);

  // Quiz State
  quizPaneOpen = signal(false);

  // Generator State
  generatorPaneOpen = signal(false);
  imageGeneratorPaneOpen = signal(false);

  // Refiner State
  refinerPaneOpen = signal(false);
  refinerDraft = signal<any>(null);

  // Mermaid Generator State
  mermaidGeneratorPaneOpen = signal(false);

  imageRefinerPaneOpen = signal(false);
  imageRefinerDraft = signal<any>(null);

  questionsAnalysisPaneOpen = signal(false);
  questionsSearchSidebarOpen = signal(false);

  // Responsive state
  isSmallOrTablet = computed(() => this.breakpointObserver.isSmallScreen() || this.breakpointObserver.isTabletScreen());
  drawerMode = computed(() => (this.isSmallOrTablet() ? 'over' : 'side'));

  activeMode = computed<
    'ai' | 'quiz' | 'generator' | 'mermaid-generator' | 'refiner' | 'image-generator' | 'image-refiner' | 'questions-analysis' | 'questions-search-sidebar' | null
  >(() => {
    if (this.aiPaneOpen()) return 'ai';
    if (this.quizPaneOpen()) return 'quiz';
    if (this.generatorPaneOpen()) return 'generator';
    if (this.mermaidGeneratorPaneOpen()) return 'mermaid-generator';
    if (this.imageGeneratorPaneOpen()) return 'image-generator';
    if (this.imageRefinerPaneOpen()) return 'image-refiner';
    if (this.refinerPaneOpen()) return 'refiner';
    if (this.questionsAnalysisPaneOpen()) return 'questions-analysis';
    if (this.questionsSearchSidebarOpen()) return 'questions-search-sidebar';
    return null;
  });

  isDrawerOpen = computed(() => this.activeMode() !== null);

  private wasDrawerOpen = false;

  private elementRef = inject(ElementRef);
  @ViewChild('drawer', { read: ElementRef }) drawerElement!: ElementRef;

  private touchStartX = 0;
  private touchStartY = 0;
  private readonly swipeThreshold = 50;
  private readonly edgeThreshold = 120;
  isDragging = signal(false);
  private touchStartOpened = false;
  private currentTranslateX = 0;

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (!this.isSmallOrTablet()) return;
    if (window.innerWidth < 599 && this.activeMode() !== 'ai' && this.activeMode() !== null) return;
    if (event.touches.length > 0) {
      const target = event.target as HTMLElement;
      if (
        target.closest('input') ||
        target.closest('textarea') ||
        target.hasAttribute('contenteditable') ||
        target.closest('.ql-editor') ||
        target.closest('.latex-textarea') ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('app-whiteboard') ||
        target.closest('app-whiteboard-part') ||
        target.closest('.whiteboard-container') ||
        target.closest('.whiteboard-body') ||
        target.closest('canvas') ||
        target.closest('app-quill-toolbar') ||
        target.closest('.ql-toolbar') ||
        target.closest('.whiteboard-header')
      ) {
        this.touchStartX = 0;
        this.touchStartY = 0;
        return;
      }
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchStartOpened = this.isDrawerOpen();
    }
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (!this.isSmallOrTablet()) return;
    if (window.innerWidth < 599 && this.activeMode() !== 'ai' && this.activeMode() !== null) return;
    if (this.touchStartX === 0) return;
    if (event.touches.length > 0) {
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const diffX = currentX - this.touchStartX;
      const diffY = Math.abs(currentY - this.touchStartY);

      if (Math.abs(diffX) > diffY * 1.2) {
        const screenWidth = window.innerWidth;
        let drawerWidth = this.drawerElement?.nativeElement?.offsetWidth;
        if (!drawerWidth || drawerWidth === 0) {
          drawerWidth = Math.min(400, window.innerWidth * 0.85);
        }

        if (!this.touchStartOpened) {
          // Opening swipe left (diffX is negative, starting near the right edge)
          if (this.touchStartX >= screenWidth - this.edgeThreshold && diffX < 0) {
            this.isDragging.set(true);
            if (event.cancelable) {
              event.preventDefault();
            }
            // Translate from drawerWidth to 0
            const translateX = Math.max(0, drawerWidth + diffX);
            this.currentTranslateX = translateX;
            this.applyStyles(translateX, drawerWidth);
          }
        } else {
          // Closing swipe right (diffX is positive)
          if (diffX > 0) {
            this.isDragging.set(true);
            if (event.cancelable) {
              event.preventDefault();
            }
            // Translate from 0 to drawerWidth
            const translateX = Math.min(drawerWidth, diffX);
            this.currentTranslateX = translateX;
            this.applyStyles(translateX, drawerWidth);
          }
        }
      }
    }
  }

  private applyStyles(translateX: number, drawerWidth: number) {
    if (this.drawerElement?.nativeElement) {
      this.drawerElement.nativeElement.style.transform = `translate3d(${translateX}px, 0, 0)`;
      this.drawerElement.nativeElement.style.transition = 'none';
      this.drawerElement.nativeElement.style.visibility = 'visible';
    }
    // Update container backdrop opacity if present
    const progress = (drawerWidth - translateX) / drawerWidth; // 0 to 1
    const backdrop = this.elementRef.nativeElement.querySelector('.mat-drawer-backdrop');
    if (backdrop) {
      backdrop.style.opacity = `${progress * 0.6}`; // Material default max opacity is usually around 0.6
      backdrop.style.transition = 'none';
      backdrop.style.visibility = 'visible';
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    if (!this.isSmallOrTablet()) return;
    if (this.touchStartX === 0 && this.touchStartY === 0) return;

    if (this.isDragging()) {
      this.isDragging.set(false);

      if (this.drawerElement?.nativeElement) {
        this.drawerElement.nativeElement.style.transform = '';
        this.drawerElement.nativeElement.style.transition = '';
        this.drawerElement.nativeElement.style.visibility = '';
      }
      const backdrop = this.elementRef.nativeElement.querySelector('.mat-drawer-backdrop');
      if (backdrop) {
        backdrop.style.opacity = '';
        backdrop.style.transition = '';
        backdrop.style.visibility = '';
      }

      let drawerWidth = this.drawerElement?.nativeElement?.offsetWidth;
      if (!drawerWidth || drawerWidth === 0) {
        drawerWidth = Math.min(400, window.innerWidth * 0.85);
      }
      const progress = (drawerWidth - this.currentTranslateX) / drawerWidth; // 0 (closed) to 1 (open)

      if (!this.touchStartOpened) {
        // Opening swipe: succeed if dragged in more than 30% of width
        if (progress > 0.3) {
          this.aiPaneOpen.set(true);
        } else {
          this.closeAllPanes();
        }
      } else {
        // Closing swipe: succeed if closed more than 30% of width
        if (progress < 0.7) {
          this.closeAllPanes();
        } else {
          this.aiPaneOpen.set(true); // remain open
        }
      }
    } else {
      // Fallback for quick swipes
      if (event.changedTouches.length > 0) {
        const screenWidth = window.innerWidth;
        const touchEndX = event.changedTouches[0].clientX;
        const touchEndY = event.changedTouches[0].clientY;
        const diffX = touchEndX - this.touchStartX;
        const diffY = touchEndY - this.touchStartY;

        if (Math.abs(diffX) > Math.abs(diffY) * 1.5) {
          if (diffX < -this.swipeThreshold) {
            // Swipe Left (Open right drawer)
            if (this.touchStartX >= screenWidth - this.edgeThreshold && !this.isDrawerOpen()) {
              this.aiPaneOpen.set(true);
            }
          } else if (diffX > this.swipeThreshold) {
            // Swipe Right (Close right drawer)
            if (this.isDrawerOpen()) {
              this.closeAllPanes();
            }
          }
        }
      }
    }

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.currentTranslateX = 0;
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    if (this.isDrawerOpen() && this.isSmallOrTablet()) {
      // The back button was pressed on mobile. The dummy state is already popped.
      // We close all panes to keep the user on the same page.
      this.closeAllPanes();
    }
  }

  closeAllPanes() {
    this.closeVideoDrawer();
    this.aiPaneOpen.set(false);
    this.quizPaneOpen.set(false);
    this.generatorPaneOpen.set(false);
    this.mermaidGeneratorPaneOpen.set(false);
    this.imageGeneratorPaneOpen.set(false);
    this.refinerPaneOpen.set(false);
    this.imageRefinerPaneOpen.set(false);
    this.questionsAnalysisPaneOpen.set(false);
    this.questionsSearchSidebarOpen.set(false);
  }

  constructor() {
    effect(() => {
      const isOpen = this.isDrawerOpen();
      const isMobile = this.isSmallOrTablet();

      // Ensure we only manipulate history state on mobile devices
      if (isMobile) {
        if (isOpen && !this.wasDrawerOpen) {
          // Drawer opened: Push a dummy state so the physical back button can close it
          window.history.pushState({ drawer: 'open' }, '');
        } else if (!isOpen && this.wasDrawerOpen) {
          // Drawer closed programmatically: pop the dummy state if it's still there
          if (window.history.state?.drawer === 'open') {
            window.history.back();
          }
        }
      }
      this.wasDrawerOpen = isOpen;
    });

    effect(() => {
      this.contentStateService.isGeneratorOpen.set(this.generatorPaneOpen());
    });

    this.contentStateService.openAIPaneCommand$.subscribe((data) => {
      this.closeVideoDrawer();
      this.aiPaneData.set(data);
      this.aiPaneOpen.set(true);
      this.quizPaneOpen.set(false);
      this.generatorPaneOpen.set(false);
      this.mermaidGeneratorPaneOpen.set(false);
      this.imageGeneratorPaneOpen.set(false);
      this.refinerPaneOpen.set(false);
      this.questionsAnalysisPaneOpen.set(false);
    });

    this.contentStateService.openQuestionsAnalysisSidebarCommand$.pipe(takeUntilDestroyed()).subscribe((open) => {
      this.closeVideoDrawer();
      this.questionsAnalysisPaneOpen.set(open);
      if (open) {
        this.aiPaneOpen.set(false);
        this.quizPaneOpen.set(false);
        this.generatorPaneOpen.set(false);
        this.imageGeneratorPaneOpen.set(false);
        this.refinerPaneOpen.set(false);
        this.questionsSearchSidebarOpen.set(false);
      }
    });

    this.contentStateService.openQuestionsSearchSidebarCommand$.pipe(takeUntilDestroyed()).subscribe((open) => {
      this.closeVideoDrawer();
      this.questionsSearchSidebarOpen.set(open);
      if (open) {
        this.aiPaneOpen.set(false);
        this.quizPaneOpen.set(false);
        this.generatorPaneOpen.set(false);
        this.imageGeneratorPaneOpen.set(false);
        this.refinerPaneOpen.set(false);
        this.questionsAnalysisPaneOpen.set(false);
      }
    });

    this.contentStateService.openQuizGeneratorCommand$.subscribe(() => {
      this.closeVideoDrawer();
      this.quizPaneOpen.set(true);
      this.aiPaneOpen.set(false);
      this.generatorPaneOpen.set(false);
      this.mermaidGeneratorPaneOpen.set(false);
      this.imageGeneratorPaneOpen.set(false);
      this.refinerPaneOpen.set(false);
      this.questionsAnalysisPaneOpen.set(false);
    });

    this.contentStateService.openContentGeneratorCommand$.pipe(takeUntilDestroyed()).subscribe((data: any) => {
      this.closeVideoDrawer();
      if (data) {
        if (data.isAuto && this.isSmallOrTablet()) {
          this.generatorPaneOpen.set(false);
          return;
        }
        this.generatorPaneOpen.set(true);
        this.aiPaneOpen.set(false);
        this.quizPaneOpen.set(false);
        this.mermaidGeneratorPaneOpen.set(false);
        this.imageGeneratorPaneOpen.set(false);
        this.refinerPaneOpen.set(false);
        this.questionsAnalysisPaneOpen.set(false);
      } else {
        this.generatorPaneOpen.set(false);
      }
    });

    this.contentStateService.openMermaidGeneratorCommand$.pipe(takeUntilDestroyed()).subscribe((data: boolean) => {
      this.closeVideoDrawer();
      if (data) {
        this.mermaidGeneratorPaneOpen.set(true);
        this.aiPaneOpen.set(false);
        this.quizPaneOpen.set(false);
        this.generatorPaneOpen.set(false);
        this.imageGeneratorPaneOpen.set(false);
        this.refinerPaneOpen.set(false);
        this.questionsAnalysisPaneOpen.set(false);
      } else {
        this.mermaidGeneratorPaneOpen.set(false);
      }
    });

    this.contentStateService.openContentRefinerCommand$.subscribe((draft) => {
      this.closeVideoDrawer();
      this.refinerDraft.set(draft);
      this.refinerPaneOpen.set(true);
      this.aiPaneOpen.set(false);
      this.quizPaneOpen.set(false);
      this.generatorPaneOpen.set(false);
      this.mermaidGeneratorPaneOpen.set(false);
      this.imageGeneratorPaneOpen.set(false);
      this.questionsAnalysisPaneOpen.set(false);
    });

    this.contentStateService.openImageGeneratorCommand$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.closeVideoDrawer();
      this.imageGeneratorPaneOpen.set(true);
      this.aiPaneOpen.set(false);
      this.quizPaneOpen.set(false);
      this.generatorPaneOpen.set(false);
      this.mermaidGeneratorPaneOpen.set(false);
      this.refinerPaneOpen.set(false);
      this.imageRefinerPaneOpen.set(false);
      this.questionsAnalysisPaneOpen.set(false);
    });

    this.contentStateService.openImageRefinerCommand$.pipe(takeUntilDestroyed()).subscribe((draft) => {
      this.closeVideoDrawer();
      this.imageRefinerDraft.set(draft);
      this.imageRefinerPaneOpen.set(true);
      this.aiPaneOpen.set(false);
      this.quizPaneOpen.set(false);
      this.generatorPaneOpen.set(false);
      this.mermaidGeneratorPaneOpen.set(false);
      this.refinerPaneOpen.set(false);
      this.imageGeneratorPaneOpen.set(false);
      this.questionsAnalysisPaneOpen.set(false);
    });

    effect(() => {
      if (this.videoDrawerOpened()) {
        this.closeAllContentPanes();
      }
    });
  }

  closeVideoDrawer() {
    if (this.videoCallStateService.drawerOpened()) {
      this.videoCallStateService.closeDrawer();
    }
  }

  closeAllContentPanes() {
    this.aiPaneOpen.set(false);
    this.quizPaneOpen.set(false);
    this.generatorPaneOpen.set(false);
    this.mermaidGeneratorPaneOpen.set(false);
    this.imageGeneratorPaneOpen.set(false);
    this.refinerPaneOpen.set(false);
    this.imageRefinerPaneOpen.set(false);
    this.questionsAnalysisPaneOpen.set(false);
    this.questionsSearchSidebarOpen.set(false);
  }

  closeAiPane() {
    this.aiPaneOpen.set(false);
  }

  closeQuizPane() {
    this.quizPaneOpen.set(false);
  }

  closeGeneratorPane() {
    this.generatorPaneOpen.set(false);
  }

  closeMermaidGeneratorPane() {
    this.mermaidGeneratorPaneOpen.set(false);
  }

  closeImageGeneratorPane() {
    this.imageGeneratorPaneOpen.set(false);
  }

  closeImageRefinerPane() {
    this.imageRefinerPaneOpen.set(false);
    this.imageGeneratorPaneOpen.set(true);
  }

  closeRefinerPane() {
    this.refinerPaneOpen.set(false);
    this.generatorPaneOpen.set(true);
  }

  closeQuestionsAnalysisPane() {
    this.questionsAnalysisPaneOpen.set(false);
  }

  closeQuestionsSearchSidebarPane() {
    this.questionsSearchSidebarOpen.set(false);
  }

  handleRefinerUpdate(updatedDraft: any) {
    this.refinerDraft.set(updatedDraft);
    // Also notify CanvasEditor to update content (via ContentStateService -> openContentGeneratorDraftCommand)
    this.contentStateService.openContentGeneratorDraft(updatedDraft);
  }

  handleImageRefinerUpdate(updatedDraft: any) {
    this.imageRefinerDraft.set(updatedDraft);
  }

  handleImageSelection(data: { url: string; alt: string; storagePath: string }) {
    if (!data.storagePath) {
      console.error('No storage path provided for image insertion');
      return;
    }

    const insertId = this.contentStateService.highlightedChunkId() || null;
    const fileName = data.alt.replace(/\s+/g, '_').toLowerCase() + '.png';

    this.contentStateService.setUploadState({ status: 'creating', progress: 100, insertId, fileName });

    const copyData: CopyImageAndCreateChunkData = {
      sourceGcsPath: data.storagePath,
      contentID: this.contentId(),
      insertBelowID: insertId || undefined,
      insertAsFirst: !insertId ? true : false,
      language: this.translate.currentLang,
    };

    this.chunkService.createChunkFromGCSCopy(copyData).subscribe({
      next: () => {
        this.contentStateService.setUploadState(null);
        this.toast.snackbar.open(this.translate.instant('Global.ImageInserted'), 'Close', { duration: 2000 });
        this.closeImageRefinerPane();
        this.closeImageGeneratorPane();
      },
      error: (err) => {
        console.error('Copy failed', err);
        this.errorHandler.handle401Error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
        this.contentStateService.setUploadState(null);
      },
    });
  }
}
